import { useState } from "react";
import {
  Download,
  FolderOpen,
  HelpCircle,
  LogIn,
  LogOut,
  Menu,
  PanelRight,
  Sliders,
  Upload,
  User,
} from "lucide-react";
import { useStore } from "../state/store";
import { Button } from "./ui/Button";
import { ScenarioMenu } from "./ScenarioMenu";
import { ImportExportDialog } from "./ImportExportDialog";
import { LoginDialog } from "./LoginDialog";
import { HelpDialog } from "./HelpDialog";
import { ViewSettingsPopover } from "./ViewSettingsPopover";

export function Topbar() {
  const name = useStore((s) => s.scenario.name);
  const setName = useStore((s) => s.setScenarioName);
  const isPublic = useStore((s) => s.scenario.isPublic);
  const authUser = useStore((s) => s.authUser);
  const logout = useStore((s) => s.logoutAdmin);
  const setLeftPanelOpen = useStore((s) => s.setLeftPanelOpen);
  const setRightPanelOpen = useStore((s) => s.setRightPanelOpen);
  const leftPanelOpen = useStore((s) => s.leftPanelOpen);
  const rightPanelOpen = useStore((s) => s.rightPanelOpen);

  const [menuOpen, setMenuOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [viewSettingsOpen, setViewSettingsOpen] = useState(false);

  return (
    <header className="border-b border-border bg-card flex items-center px-2 md:px-4 py-2 gap-2 md:gap-3 relative">
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={() => setLeftPanelOpen(!leftPanelOpen)}
        title="Open scenario- en blokkenpaneel"
      >
        <Menu size={16} />
      </Button>
      <div className="flex items-baseline gap-3">
        <h1 className="text-lg font-semibold tracking-tight hidden sm:block">
          Schaduwtool
        </h1>
      </div>
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <label
          htmlFor="scenario-name"
          className="text-sm text-muted-foreground hidden md:inline"
        >
          Scenario:
        </label>
        <input
          id="scenario-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-background border border-input rounded-md px-2 py-1 text-sm w-full md:w-72"
        />
        {isPublic && (
          <span
            className="hidden sm:inline-flex text-[11px] uppercase tracking-wider bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded"
            title="Dit is een publiek scenario; bewerken vereist admin-rechten"
          >
            publiek
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 md:gap-1.5 relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMenuOpen((v) => !v)}
          title="Scenario's bekijken"
        >
          <FolderOpen size={14} /> <span className="hidden sm:inline">Scenario's</span>
        </Button>
        <ScenarioMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setImportOpen(true)}
          title="Scenario importeren"
        >
          <Upload size={14} />
          <span className="hidden md:inline">Importeer</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExportOpen(true)}
          title="Scenario exporteren"
        >
          <Download size={14} />
          <span className="hidden md:inline">Exporteer</span>
        </Button>

        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setViewSettingsOpen((v) => !v)}
            title="Weergave-instellingen"
          >
            <Sliders size={16} />
          </Button>
          <ViewSettingsPopover
            open={viewSettingsOpen}
            onClose={() => setViewSettingsOpen(false)}
          />
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setHelpOpen(true)}
          title="Uitleg en help"
        >
          <HelpCircle size={16} />
        </Button>

        {authUser ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => logout()}
            title={`Uitloggen (${authUser.username})`}
          >
            <LogOut size={14} /> <span className="hidden sm:inline">{authUser.username}</span>
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLoginOpen(true)}
            title="Admin inloggen"
          >
            <LogIn size={14} /> <span className="hidden sm:inline">Login</span>
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setRightPanelOpen(!rightPanelOpen)}
          title="Open eigenschappen-paneel"
        >
          <PanelRight size={16} />
        </Button>

        <span className="hidden lg:inline-flex items-center gap-1 text-xs text-muted-foreground ml-1">
          <User size={12} />
          {authUser ? "admin" : "publiek"}
        </span>
      </div>

      <ImportExportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        mode="import"
      />
      <ImportExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        mode="export"
      />
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
}
